import { ApolloClient, ApolloError, createHttpLink, InMemoryCache } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { CurrentUserQuery as Query } from './current_user_query'
import { CurrentUserQueryQuery as CurrentUserQuery } from './__generated__/current_user_query'
import { FullPage as Loading } from '@/components/loading'
import posthog from 'posthog-js'

type CurrentUser = CurrentUserQuery['currentUser']
type Company = CurrentUserQuery['currentUser']['companies'][0]

type CurrentTenantCtx = {
  currentUser: CurrentUser | null
  currentCompany: Company | null
  setCurrentCompany: (company: Company) => void
  availableCompanies: Company[]
}

interface Props {
  children: ReactNode
}

// eslint-disable-next-line react-refresh/only-export-components
export const CurrentTenantContext = createContext<CurrentTenantCtx>({
  currentUser: null, currentCompany: null, setCurrentCompany: () => {}, availableCompanies: []
})

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_RIPPLE_GRAPHQL_ENDPOINT,
})

const fetchCurrentUser = (token: string, callback: (currentUser: CurrentUser) => void, retries: number = 0) => {
  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : ''
      },
    }
  })

  const client = new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache
  })

  client.query<CurrentUserQuery>({ query: Query })
    .then(result => {
      callback(result.data.currentUser)
    })
    .catch((error: ApolloError) => {
      if (error.message.includes('404')) {
        // 404 because the session hadn't landed properly yet
        if (retries < 2) {
          setTimeout(() => fetchCurrentUser(token, callback, retries + 1), 200 + (retries * 200))
        } else {
          console.log('Retried too many times, giving up')
        }
      } else {
        console.log(error)
      }
    })
}

export const CurrentTenantProvider = ({
  children,
}: Props) => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null)
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([])
  const [currentCompany, setCurrentCompanyInternal] = useState<Company | null>(null)

  const { token, error, logIn } = useContext<IAuthContext>(AuthContext)

  const setCurrentCompany = (company: Company) => {
    setCurrentCompanyInternal(company)
    posthog.group('company', company.id, { name: company.name })
    localStorage.setItem('currentCompanyId', company.id)
  }

  const handleCurrentUserChange = useCallback((currentUser: CurrentUser) => {
    setCurrentUser(currentUser)
    setAvailableCompanies(currentUser.companies)
    setCurrentCompany(currentUser.companies.find(c => c.id == localStorage.getItem('currentCompanyId')) || currentUser.companies[0])

    posthog.identify(
      currentUser.id,
      { email: currentUser.email, name: currentUser.displayName }
    )
  }, [])

  useEffect(() => {
    if (token.length == 0) return

    fetchCurrentUser(token, handleCurrentUserChange)
  }, [token, handleCurrentUserChange])

  useEffect(() => {
    if (error?.match(/Bad authorization state/)) { logIn(undefined, undefined, 'redirect') }
  }, [error, logIn])

  if (!currentUser) return <Loading />

  return (
    <CurrentTenantContext.Provider value={{ currentUser, currentCompany, setCurrentCompany, availableCompanies }}>
      {children}
    </CurrentTenantContext.Provider>
  )
}
